<script>
  import DockContextMenu from './DockContextMenu.svelte';
  import Icon from './Icon.svelte';
  import { APPS, appById } from '../os/apps.js';
  import {
    windows,
    activateApp,
    closeApp,
    launchApp,
    minimizeApp,
    restoreWindow,
    windowsForApp,
  } from '../os/windows.svelte.js';
  import { portfolio, savePortfolio } from '../os/portfolio.svelte.js';
  import { session } from '../os/session.svelte.js';
  import { recentState } from '../os/recents.svelte.js';

  const dockPos = $derived(portfolio.settings.dockPosition ?? 'bottom');

  const isAvailable = (app) => app && (!app.adminOnly || session.role === 'admin');
  const isRunning = (id) => windows.some((win) => win.appId === id);

  let dragItemId = $state(null);
  let dragOverItemId = $state(null);

  const visibleApps = $derived.by(() => {
    const pinned = APPS.filter((app) => app.dock && isAvailable(app));
    const running = APPS.filter((app) => !app.dock && isRunning(app.id) && isAvailable(app));
    const all = [...pinned, ...running];
    const order = portfolio.settings.dockOrder;
    if (order && Array.isArray(order)) {
      const ordered = order.map(id => all.find(a => a.id === id)).filter(Boolean);
      const remaining = all.filter(a => !order.includes(a.id));
      return [...ordered, ...remaining];
    }
    return all;
  });

  const recentApps = $derived(
    recentState.items
      .filter(item => item.kind === 'app')
      .map(item => appById(item.id))
      .filter(Boolean)
      .filter((app, i, arr) => app.dock !== true && arr.indexOf(app) === i)
      .slice(0, 3)
  );

  const minimizedWindows = $derived(
    windows
      .filter((win) => win.minimized)
      .sort((a, b) => a.z - b.z)
  );

  let bouncingApps = $state({});
  let contextMenu = $state(null);

  async function handleActivate(app) {
    if (!app) return;
    const isNew = !isRunning(app.id);
    if (isNew) {
      bouncingApps[app.id] = true;
      setTimeout(() => (bouncingApps[app.id] = false), 900);
    }
    await activateApp(app);
  }

  function openContextMenu(event, app) {
    event.preventDefault();
    contextMenu = {
      app,
      x: Math.min(event.clientX, globalThis.innerWidth - 210),
      y: Math.max(8, event.clientY - 210),
    };
  }

  function runContext(action, value) {
    if (action === 'close') { contextMenu = null; return; }
    const app = contextMenu?.app;
    if (!app) return;
    if (action === 'new') launchApp(app);
    if (action === 'show') activateApp(app);
    if (action === 'hide') minimizeApp(app.id);
    if (action === 'quit') closeApp(app.id);
    if (action === 'window') restoreWindow(value);
    contextMenu = null;
  }
</script>

<svelte:window onpointerdown={() => { if (contextMenu) contextMenu = null; }} />

<DockContextMenu {contextMenu} {isRunning} onaction={runContext} />

<div
  class="dock"
  class:autohide={portfolio.settings.autoHideDock}
  class:no-magnification={!portfolio.settings.dockMagnification}
  class:dock-left={dockPos === 'left'}
  class:dock-right={dockPos === 'right'}
  style={`--dock-icon-size:${portfolio.settings.dockSize ?? 62}px`}
  role="toolbar"
  aria-label="Dock"
>
  {#each visibleApps as app}
    <button
      class="dock-item"
      class:bouncing={bouncingApps[app.id]}
      class:drag-over={dragOverItemId === app.id}
      draggable="true"
      title={app.label}
      aria-label={`${app.label}${isRunning(app.id) ? ', running' : ''}`}
      onclick={() => handleActivate(app)}
      oncontextmenu={(event) => openContextMenu(event, app)}
      ondragstart={(e) => { e.dataTransfer.effectAllowed = 'move'; dragItemId = app.id; }}
      ondragenter={() => (dragOverItemId = app.id)}
      ondragover={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
      ondragleave={() => { if (dragOverItemId === app.id) dragOverItemId = null; }}
      ondrop={(e) => { e.preventDefault(); if (dragItemId && dragItemId !== app.id) { const order = visibleApps.map(a => a.id); const from = order.indexOf(dragItemId); const to = order.indexOf(app.id); if (from !== -1 && to !== -1) { order.splice(from, 1); order.splice(to, 0, dragItemId); portfolio.settings.dockOrder = order; savePortfolio(); } } dragOverItemId = null; dragItemId = null; }}
      ondragend={() => { dragItemId = null; dragOverItemId = null; }}
    >
      <span class="dock-icon"><Icon name={app.icon} size={portfolio.settings.dockSize ?? 62} /></span>
      <span class="run-dot" class:on={isRunning(app.id)} aria-hidden="true"></span>
    </button>
  {/each}

  {#if recentApps.length}
    <span class="dock-divider" aria-hidden="true"></span>
    {#each recentApps as app}
      <button
        class="dock-item dock-recent"
        title={app.label}
        aria-label={`${app.label} - recent`}
        onclick={() => handleActivate(app)}
        oncontextmenu={(event) => openContextMenu(event, app)}
      >
        <span class="dock-icon"><Icon name={app.icon} size={portfolio.settings.dockSize ?? 62} /></span>
      </button>
    {/each}
  {/if}

  <span class="dock-divider" aria-hidden="true"></span>

  {#each minimizedWindows as win (win.id)}
    {@const app = appById(win.appId)}
    <button
      class="dock-item dock-minimized"
      title={`${win.title} - minimized`}
      aria-label={`Restore ${win.title}`}
      onclick={() => restoreWindow(win.id)}
    >
      <span class="dock-window-preview">
        <span class="dock-window-bar"></span>
        <Icon name={app?.icon ?? 'document'} size={30} />
        <small>{win.title}</small>
      </span>
    </button>
  {/each}

  <button
    class="dock-item dock-trash"
    title="Trash"
    aria-label={`Trash${isRunning('trash') ? ', running' : ''}`}
    onclick={() => handleActivate(appById('trash'))}
    oncontextmenu={(event) => openContextMenu(event, appById('trash'))}
  >
    <span class="dock-icon"><Icon name="trash" size={portfolio.settings.dockSize ?? 62} /></span>
    <span class="run-dot" class:on={isRunning('trash')} aria-hidden="true"></span>
  </button>
</div>

<style>
  .dock {
    position: absolute;
    left: 50%;
    bottom: -1px;
    transform: translateX(-50%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 6px;
    width: max-content;
    max-width: 98vw;
    min-height: 104px;
    padding: 0 34px 17px;
    border-radius: 0;
    background: transparent;
    border: 0;
    z-index: 9999990;
    transition: transform 0.22s ease, opacity 0.22s ease, bottom 0.22s ease;
    isolation: isolate;
    perspective: 600px;
  }
  .dock::before {
    content: "";
    position: absolute;
    left: 7px;
    right: 7px;
    bottom: 0;
    height: 67px;
    clip-path: polygon(4.5% 0, 95.5% 0, 100% 100%, 0 100%);
    background:
      linear-gradient(102deg, transparent 0 3.8%, rgba(255,255,255,0.22) 4.4%, transparent 8%, transparent 92%, rgba(255,255,255,0.18) 95.5%, transparent 100%),
      linear-gradient(to bottom,
        rgba(247, 251, 255, 0.92) 0%,
        rgba(187, 197, 208, 0.88) 28%,
        rgba(112, 121, 132, 0.9) 29%,
        rgba(174, 184, 195, 0.76) 58%,
        rgba(74, 82, 92, 0.9) 100%);
    border-top: 2px solid rgba(255,255,255,0.9);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.96),
      inset 0 -2px 0 rgba(0,0,0,0.65),
      inset 2px 0 0 rgba(255,255,255,0.35),
      inset -2px 0 0 rgba(0,0,0,0.3),
      inset 0 18px 18px rgba(255,255,255,0.18),
      inset 0 -22px 26px rgba(0,0,0,0.32),
      0 0 0 1px rgba(0,0,0,0.85),
      0 13px 25px rgba(0,0,0,0.55);
    backdrop-filter: blur(9px) saturate(1.2);
    pointer-events: none;
    z-index: -2;
  }
  .dock::after {
    content: "";
    position: absolute;
    left: 34px;
    right: 34px;
    bottom: 5px;
    height: 38px;
    clip-path: polygon(1.5% 0, 98.5% 0, 100% 100%, 0 100%);
    background:
      radial-gradient(ellipse at center top, rgba(255,255,255,0.32), transparent 46%),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.2) 0 2px, transparent 2px 12px),
      linear-gradient(to bottom, rgba(255,255,255,0.14), rgba(255,255,255,0));
    opacity: 0.62;
    pointer-events: none;
    z-index: -1;
  }
  .dock.autohide {
    bottom: 0;
    opacity: 0.2;
    transform: translateX(-50%) translateY(calc(100% - 12px));
  }
  .dock.autohide:hover,
  .dock.autohide:focus-within {
    bottom: 0;
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  .dock.autohide .dock-item,
  .dock.autohide .dock-divider {
    pointer-events: none;
  }
  .dock.autohide:hover .dock-item,
  .dock.autohide:focus-within .dock-item,
  .dock.autohide:hover .dock-divider,
  .dock.autohide:focus-within .dock-divider {
    pointer-events: auto;
  }
  .dock-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    flex: 0 1 calc(var(--dock-icon-size, 62px) + 3px);
    width: calc(var(--dock-icon-size, 62px) + 3px);
    min-width: 34px;
    height: 92px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    transform-origin: bottom center;
    transition: filter 0.14s ease;
    z-index: 2;
  }
  .dock:hover .dock-item {
    filter: saturate(1.08) contrast(1.02);
  }
  .dock-item:hover {
    z-index: 3;
  }
  .dock:hover .dock-item:has(+ .dock-item:hover) .dock-icon,
  .dock:hover .dock-item:hover + .dock-item .dock-icon {
    transform: scale(1.1) translateY(-4px);
  }
  .dock:hover .dock-item:has(+ .dock-item + .dock-item:hover) .dock-icon,
  .dock:hover .dock-item:hover + .dock-item + .dock-item .dock-icon {
    transform: scale(1.04) translateY(-2px);
  }
  .dock-item:hover .dock-icon {
    transform: scale(1.24) translateY(-13px);
    z-index: 3;
  }
  .dock-item:active .dock-icon { transform: scale(1.14) translateY(-6px); }
  .dock-item.drag-over .dock-icon {
    transform: scale(1.24) translateY(-13px) !important;
    filter: brightness(1.3);
  }
  .dock-item.drag-over { cursor: grabbing; }
  .dock-item.bouncing .dock-icon {
    animation: dock-bounce 0.6s ease-in-out infinite;
  }
  @keyframes dock-bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-24px) scaleY(1.08);
    }
  }
  .dock-icon {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    max-width: var(--dock-icon-size, 58px);
    aspect-ratio: 1 / 1;
    transform-origin: bottom center;
    pointer-events: none;
    transition: transform 0.18s ease-out, filter 0.18s ease;
    filter:
      drop-shadow(0 7px 4px rgba(0,0,0,0.46))
      drop-shadow(0 1px 0 rgba(255,255,255,0.28));
    -webkit-box-reflect: below -8px linear-gradient(transparent 38%, rgba(255,255,255,0.34) 68%, rgba(255,255,255,0.04) 100%);
  }
  .dock.no-magnification .dock-item:hover .dock-icon,
  .dock.no-magnification:hover .dock-item:has(+ .dock-item:hover) .dock-icon,
  .dock.no-magnification:hover .dock-item:hover + .dock-item .dock-icon,
  .dock.no-magnification:hover .dock-item:has(+ .dock-item + .dock-item:hover) .dock-icon,
  .dock.no-magnification:hover .dock-item:hover + .dock-item + .dock-item .dock-icon {
    transform: none;
  }
  .dock-icon :global(.os-icon) {
    width: 100% !important;
    height: auto !important;
    max-height: 100% !important;
    object-fit: contain;
  }
  .dock-trash .dock-icon :global(.os-icon) {
    width: 110% !important;
  }
  .dock-divider {
    position: relative;
    width: 3px;
    height: 62px;
    margin: 0 4px 13px;
    background:
      linear-gradient(to bottom, rgba(255,255,255,0), rgba(52,56,62,0.72) 28%, rgba(52,56,62,0.72) 72%, rgba(255,255,255,0)),
      linear-gradient(to right, rgba(255,255,255,0.62), rgba(0,0,0,0.28));
    box-shadow: 1px 0 0 rgba(255,255,255,0.45), -1px 0 0 rgba(0,0,0,0.18);
    transform: skewX(-8deg);
    z-index: 2;
  }
  .dock-minimized {
    flex-basis: 58px;
    width: 58px;
    min-width: 38px;
  }
  .dock-recent .dock-icon {
    opacity: 0.7;
    filter: saturate(0.5);
  }
  .dock-recent:hover .dock-icon {
    opacity: 1;
    filter: saturate(1);
  }
  .dock-window-preview {
    position: relative;
    width: 52px;
    height: 44px;
    margin-bottom: 16px;
    display: grid;
    place-items: center;
    overflow: hidden;
    color: #f1f4f4;
    background: #272c2c;
    border: 1px solid #111;
    box-shadow: inset 0 1px rgba(255,255,255,.17), 0 4px 6px rgba(0,0,0,.45);
    transform: perspective(90px) rotateX(4deg);
  }
  .dock-window-preview :global(.os-icon) {
    width: 26px !important;
    height: 26px !important;
  }
  .dock-window-preview small {
    position: absolute;
    right: 2px;
    bottom: 1px;
    left: 2px;
    overflow: hidden;
    color: #d8dddd;
    font: 7px var(--font);
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .dock-window-bar {
    position: absolute;
    inset: 0 0 auto;
    height: 6px;
    background: linear-gradient(#777e7e, #434949);
    border-bottom: 1px solid #161919;
  }
  /* narrow-viewport dock tweaks live globally in app.css (keeps this file small) */
  .run-dot {
    position: absolute;
    left: 50%;
    bottom: 8px;
    width: 13px;
    height: 5px;
    margin-left: -6.5px;
    border-radius: 50%;
    background: transparent;
    pointer-events: none;
  }
  .run-dot.on {
    background: radial-gradient(ellipse at center, #ffffff 0%, #b9e3ff 36%, #4ea2ff 72%, rgba(82,132,210,0.05) 100%);
    box-shadow: 0 0 9px rgba(148, 207, 255, 0.98), 0 1px 1px rgba(0,0,0,0.25);
  }

  .dock-left, .dock-right {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: auto;
    width: max-content;
    min-height: 0;
    min-width: 86px;
    padding: 26px 0 18px;
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
  }
  .dock-left {
    left: 0;
    right: auto;
  }
  .dock-right {
    left: auto;
    right: 0;
  }
  .dock-left::before, .dock-right::before {
    left: 0;
    right: 0;
    top: 7px;
    bottom: 7px;
    width: auto;
    height: auto;
    clip-path: polygon(0 4.5%, 100% 0, 100% 100%, 0 95.5%);
    background:
      linear-gradient(0deg, transparent 0 3.8%, rgba(255,255,255,0.22) 4.4%, transparent 8%, transparent 92%, rgba(255,255,255,0.18) 95.5%, transparent 100%),
      linear-gradient(to right,
        rgba(247, 251, 255, 0.92) 0%,
        rgba(187, 197, 208, 0.88) 28%,
        rgba(112, 121, 132, 0.9) 29%,
        rgba(174, 184, 195, 0.76) 58%,
        rgba(74, 82, 92, 0.9) 100%);
    border-top: none;
    border-left: 1px solid rgba(255,255,255,0.82);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.96),
      inset 18px 0 18px rgba(255,255,255,0.18),
      inset -22px 0 26px rgba(0,0,0,0.32),
      13px 0 25px rgba(0,0,0,0.55);
  }
  .dock-left::after, .dock-right::after {
    left: 5px;
    right: 5px;
    top: 24px;
    bottom: 24px;
    width: auto;
    height: auto;
    clip-path: polygon(0 1.5%, 100% 0, 100% 100%, 0 98.5%);
  }
  .dock-left .dock-divider, .dock-right .dock-divider {
    width: 44px;
    height: 3px;
    margin: 4px 0;
    transform: none;
  }
  .dock-left.autohide, .dock-right.autohide {
    transform: translateY(-50%) translateX(calc(-100% + 12px));
    opacity: 0.2;
  }
  .dock-left.autohide:hover,
  .dock-left.autohide:focus-within {
    transform: translateY(-50%) translateX(0);
    opacity: 1;
  }
  .dock-right.autohide {
    transform: translateY(-50%) translateX(calc(100% - 12px));
  }
  .dock-right.autohide:hover,
  .dock-right.autohide:focus-within {
    transform: translateY(-50%) translateX(0);
    opacity: 1;
  }
</style>
